import React, { useState } from 'react'

import { UseMutationResult } from '@tanstack/react-query'

import {
    Prospect,
    DetailedCampaignProspect,
    Campaign,
} from '../../../../models/Outreach/types'
import {
    LeadsFilters,
    ProspectOrCampaignProspect,
} from '../../../../hooks/Outreach/useLeads'
import { ProspectsStats } from './ProspectsStats'
import { ProspectsSearchAndFilter } from './ProspectsSearchAndFilter'
import { ProspectsTable } from './ProspectsTable'
import { ProspectDetailDialog } from './ProspectDetailDialog'
import { Container } from '../../../../components/Container/Container'

interface ProspectsTabProps {
    combinedProspects: ProspectOrCampaignProspect[]
    combinedProspectsPagination: {
        count: number
        next: string | null
        previous: string | null
        currentPage: number
        pageSize: number
        totalPages: number
        summary_stats: {
            total_count: number
            in_active_campaign: number
            not_in_campaign: number
            with_email: number
            with_phone: number
            with_profile: number
        } | null
    }
    isLoadingCombinedProspects: boolean
    filters: LeadsFilters
    onFiltersChange: (filters: LeadsFilters) => void
    campaigns?: Campaign[]
    apartmentId?: number
    addProspectToCampaign: UseMutationResult<
        any,
        any,
        {
            prospectId: string | number
            campaignId: string | number
        },
        any
    >
}

export const ProspectsTab: React.FC<ProspectsTabProps> = ({
    combinedProspects,
    combinedProspectsPagination,
    isLoadingCombinedProspects,
    filters,
    onFiltersChange,
    campaigns,
    apartmentId,
    addProspectToCampaign,
}) => {
    const [
        selectedCampaignProspect,
        setSelectedCampaignProspect,
    ] = useState<DetailedCampaignProspect | null>(null)
    const [selectedProspect, setSelectedProspect] = useState<Prospect | null>(
        null,
    )

    const [dialogOpen, setDialogOpen] = useState(false)

    const handleRowClick = (
        campaignProspect: DetailedCampaignProspect | null,
        prospect: Prospect | null,
    ) => {
        setSelectedCampaignProspect(campaignProspect)
        setSelectedProspect(prospect)
        setDialogOpen(true)
    }

    const handleSearchChange = (value: string) => {
        onFiltersChange({ ...filters, search: value, page: 1 })
    }

    const handleApplyFilters = (newFilters: LeadsFilters) => {
        onFiltersChange({ ...newFilters, page: 1 })
    }

    const handleClearFilters = () => {
        onFiltersChange({
            page: 1,
            page_size: 25,
        })
    }

    const handlePageChange = (_event: unknown, newPage: number) => {
        onFiltersChange({ ...filters, page: newPage + 1 })
    }

    const handleRowsPerPageChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        onFiltersChange({
            ...filters,
            page_size: parseInt(event.target.value, 10),
            page: 1,
        })
    }

    return (
        <Container style={{ flex: 1, flexDirection: 'column' }}>
            <ProspectsStats
                combinedProspects={combinedProspects}
                pagination={combinedProspectsPagination}
                isLoading={isLoadingCombinedProspects}
            />
            <ProspectsSearchAndFilter
                filters={filters}
                onSearchChange={handleSearchChange}
                onApplyFilters={handleApplyFilters}
                onClearFilters={handleClearFilters}
                campaigns={campaigns}
            />
            <ProspectsTable
                combinedProspects={combinedProspects}
                pagination={combinedProspectsPagination}
                isLoading={isLoadingCombinedProspects}
                onRowClick={handleRowClick}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                addProspectToCampaign={addProspectToCampaign}
            />
            <ProspectDetailDialog
                open={dialogOpen}
                prospect={
                    selectedCampaignProspect?.prospect ||
                    selectedProspect ||
                    null
                }
                campaignProspect={selectedCampaignProspect}
                onClose={() => {
                    setDialogOpen(false)
                    setSelectedCampaignProspect(null)
                    setSelectedProspect(null)
                }}
                apartmentId={apartmentId}
            />
        </Container>
    )
}
